import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, FormControl, InputLabel, Select, MenuItem, TextField, Switch, Typography, FormControlLabel, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';
import ModalRegistrarFalta from './ModalRegistrarFalta';

const PontoPonto = () => {
  const [funcionario, setFuncionario] = useState('');
  const [mes, setMes] = useState('');
  const [ano, setAno] = useState('');

  const currentDate = new Date();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const [dataInicial, setDataInicial] = useState(firstDayOfMonth);
  const [dataFinal, setDataFinal] = useState(currentDate);
  const [filtroAtivo, setFiltroAtivo] = useState(false);

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [funcionariosOptions, setFuncionariosOptions] = useState([]);
  const [anosOptions, setAnosOptions] = useState([]);
  const [mesesOptions, setMesesOptions] = useState([]);

  const [modalFaltaAberto, setModalFaltaAberto] = useState(false);
  const [linhaEditando, setLinhaEditando] = useState(null);
  const [listaFaltas, setListaFaltas] = useState([]);

  const { authenticatedFetch, logout } = useAuth();

  const [editingLocked, setEditingLocked] = useState(false);
  const [recalculando, setRecalculando] = useState(false);

  const gridApiRef = useRef(null);
  const isLoadingRef = useRef(false);

  const [dialogApagarAberto, setDialogApagarAberto] = useState(false);
  const [linhaParaApagar, setLinhaParaApagar] = useState(null);

  // ========== FUNÇÕES DE COLORIZAÇÃO ==========

  const timeToMinutes = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  };

  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(Math.abs(minutes) / 60);
    const mins = Math.abs(minutes) % 60;
    return `${hours}h${mins.toString().padStart(2, '0')}min`;
  };

  const cellStyleRules = {
    saidaAlmoco: (params) => {
      const entrada = params.data.entrada;
      const saidaAlmoco = params.data.saidaIntervalo;
      if (!entrada || !saidaAlmoco) return null;
      const diff = timeToMinutes(saidaAlmoco) - timeToMinutes(entrada);
      if (diff > 360) return { backgroundColor: '#FF8C00', display: 'flex', alignItems: 'center', height: '100%' };
      return { display: 'flex', alignItems: 'center' };
    },
    entradaAlmoco: (params) => {
      const saidaAlmoco = params.data.saidaIntervalo;
      const entradaAlmoco = params.data.entradaIntervalo;
      if (!saidaAlmoco || !entradaAlmoco) return null;
      const diff = timeToMinutes(entradaAlmoco) - timeToMinutes(saidaAlmoco);
      if (diff < 60) return { backgroundColor: '#FFFF00', display: 'flex', alignItems: 'center' };
      return { display: 'flex', alignItems: 'center' };
    },
    horaFalta: (params) => {
      const horaFalta = params.data.horaFalta;
      if (horaFalta !== null && horaFalta !== undefined && horaFalta <= -15)
        return { backgroundColor: '#FF0000', color: '#FFFFFF', display: 'flex', alignItems: 'center' };
      return { display: 'flex', alignItems: 'center' };
    },
    entrada: (params) => {
      const entrada = params.data.entrada;
      const escalaEntrada = params.data.ent;
      if (!entrada || !escalaEntrada) return null;
      const diff = timeToMinutes(entrada) - timeToMinutes(escalaEntrada);
      if (diff < -5) return { backgroundColor: '#CD5C5C', display: 'flex', alignItems: 'center' };
      if (diff > 5) return { backgroundColor: '#F08080', display: 'flex', alignItems: 'center' };
      return { display: 'flex', alignItems: 'center' };
    },
    saida: (params) => {
      const saida = params.data.saida;
      const escalaSaida = params.data.sai;
      const entrada = params.data.entrada;
      if (!saida || !escalaSaida || !entrada) return null;
      let saidaMin = timeToMinutes(saida);
      let escalaSaidaMin = timeToMinutes(escalaSaida);
      const entradaMin = timeToMinutes(entrada);
      if (saidaMin >= 0 && saidaMin <= 240) saidaMin += 24 * 60;
      if (escalaSaidaMin >= 0 && escalaSaidaMin <= 240) escalaSaidaMin += 24 * 60;
      let minutos = -10;
      if (escalaSaida === '23:40') minutos = -20;
      if (saidaMin < escalaSaidaMin + minutos) return { backgroundColor: '#FFE4C4', display: 'flex', alignItems: 'center' };
      if (saidaMin > escalaSaidaMin + 10) return { backgroundColor: '#778899', display: 'flex', alignItems: 'center' };
      let diffTotal = saidaMin - entradaMin;
      if (diffTotal < 0) diffTotal += 24 * 60;
      if (diffTotal < 360) return { backgroundColor: '#D2B48C', display: 'flex', alignItems: 'center' };
      return { display: 'flex', alignItems: 'center' };
    },
  };

const tooltipGetters = {
    entrada: (params) => {
      const entrada = params.data.entrada;
      const escalaEntrada = params.data.ent;
      if (!entrada || !escalaEntrada) return undefined;
      const diff = timeToMinutes(entrada) - timeToMinutes(escalaEntrada);
      // Se entrou antes (diff é negativo), passamos o valor positivo para o formatador
      if (diff < -5) return `Entrou ${formatMinutesToHours(Math.abs(diff))} antes da escala (${escalaEntrada})`;
      if (diff > 5) return `Atrasou ${formatMinutesToHours(diff)} em relação à escala (${escalaEntrada})`;
      return undefined;
    },
    saidaIntervalo: (params) => {
      const entrada = params.data.entrada;
      const saidaAlmoco = params.data.saidaIntervalo;
      if (!entrada || !saidaAlmoco) return undefined;
      const diff = timeToMinutes(saidaAlmoco) - timeToMinutes(entrada);
      if (diff > 360) return `Passou ${formatMinutesToHours(diff)} desde a entrada (mais de 6 horas)`;
      return undefined;
    },
    entradaIntervalo: (params) => {
      const saidaAlmoco = params.data.saidaIntervalo;
      const entradaIntervalo = params.data.entradaIntervalo;
      if (!saidaAlmoco || !entradaIntervalo) return undefined;
      const diff = timeToMinutes(entradaIntervalo) - timeToMinutes(saidaAlmoco);
      if (diff < 60) return `Intervalo de apenas ${diff} minutos (mínimo 60 minutos)`;
      return undefined;
    },
    saida: (params) => {
      const saida = params.data.saida;
      const escalaSaida = params.data.sai;
      const entrada = params.data.entrada;
      if (!saida || !escalaSaida || !entrada) return undefined;
      let saidaMin = timeToMinutes(saida);
      let escalaSaidaMin = timeToMinutes(escalaSaida);
      const entradaMin = timeToMinutes(entrada);
      
      if (saidaMin >= 0 && saidaMin <= 240) saidaMin += 24 * 60;
      if (escalaSaidaMin >= 0 && escalaSaidaMin <= 240) escalaSaidaMin += 24 * 60;
      
      let minutos = -10;
      if (escalaSaida === '23:40') minutos = -20;
      
      if (saidaMin < escalaSaidaMin + minutos) {
        const diffMin = escalaSaidaMin - saidaMin;
        return `Saiu ${formatMinutesToHours(diffMin)} antes da escala (${escalaSaida})`;
      }
      if (saidaMin > escalaSaidaMin + 10) {
        const diffMin = saidaMin - escalaSaidaMin;
        return `Saiu ${formatMinutesToHours(diffMin)} depois da escala (${escalaSaida})`;
      }
      let diffTotal = Math.abs(saidaMin - entradaMin); // Garantindo valor absoluto para cálculo de jornada curta
      if (diffTotal < 360) return `Trabalhou apenas ${formatMinutesToHours(diffTotal)} (mínimo 6 horas)`;
      return undefined;
    },
    horaFalta: (params) => {
      const horaFalta = params.data.horaFalta;
      if (horaFalta !== null && horaFalta !== undefined && horaFalta <= -15)
        return `Hora falta crítica: ${Math.abs(horaFalta)} minutos`; // Exibe o número positivo no balão
      return undefined;
    },
  };

  // ========== DEFINIÇÃO DE COLUNAS COM MULTI-ORDENAÇÃO ==========

  const columnDefs = [
    { 
      headerName: 'Data', 
      field: 'data', 
      width: 105,
      sort: 'asc',      // 2ª Ordenação: Datas do dia 01 ao 31
      sortIndex: 1,     // Peso secundário número 1
      filter: 'agDateColumnFilter',
    },    
    { 
      headerName: 'Funcionário', 
      field: 'funcionario', 
      width: 120,
      sort: 'asc',      // 1ª Ordenação: Funcionário de A a Z
      sortIndex: 0      // Peso prioritário número 0
    },
    {
      headerName: 'Entrada', field: 'entrada', width: 80, editable: true,
      cellStyle: cellStyleRules.entrada, tooltipValueGetter: tooltipGetters.entrada,
    },
    {
      headerName: 'Saída Intervalo', field: 'saidaIntervalo', width: 85,
      headerClass: 'multi-line-header', wrapHeaderText: true, editable: true,
      cellStyle: cellStyleRules.saidaAlmoco, tooltipValueGetter: tooltipGetters.saidaIntervalo,
    },
    {
      headerName: 'Entrada Intervalo', field: 'entradaIntervalo', width: 85,
      headerClass: 'multi-line-header', wrapHeaderText: true, editable: true,
      cellStyle: cellStyleRules.entradaAlmoco, tooltipValueGetter: tooltipGetters.entradaIntervalo,
    },
    {
      headerName: 'Saída', field: 'saida', width: 80, editable: true,
      cellStyle: cellStyleRules.saida, tooltipValueGetter: tooltipGetters.saida,
    },
    {
      headerName: 'Falta', field: 'falta', width: 60, editable: false,
      onCellClicked: (params) => { abrirModalFalta(params.data); },
      cellStyle: { cursor: 'pointer', color: '#1976d2' },
    },
    { headerName: 'Hora Extra', field: 'horaExtra', width: 60, headerClass: 'multi-line-header', wrapHeaderText: true },
    {
      headerName: 'Hora Falta', field: 'horaFalta', width: 60,
      headerClass: 'multi-line-header', wrapHeaderText: true,
      cellStyle: cellStyleRules.horaFalta, tooltipValueGetter: tooltipGetters.horaFalta,
    },
    { headerName: 'Hora Noturna', field: 'horaNoturna', width: 80, headerClass: 'multi-line-header', wrapHeaderText: true },
    { headerName: 'Quebra', field: 'quebra', width: 100 },
    { headerName: 'Adiantamento', field: 'adiantamento', width: 120 },
    { headerName: 'Ent', field: 'ent', width: 80, editable: true },
    { headerName: 'Sai', field: 'sai', width: 80, editable: true },
    { headerName: 'Observação', field: 'observacao', colId: 'observacaoId', width: 800, editable: true },
    { headerName: 'id', field: 'id', width: 200, hide: true },
  ];

  // ========== FUNÇÕES DE DADOS ==========

  const fetchListaFaltas = async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/ponto-ponto-registrar-falta/`, { method: 'GET' });
      const data = await response.json();
      return data.faltas || [];
    } catch (err) {
      console.error('Erro ao buscar lista de faltas:', err);
      return [];
    }
  };

  const abrirModalFalta = async (linha) => {
    setLinhaEditando(linha);
    const faltas = await fetchListaFaltas();
    setListaFaltas(faltas);
    setModalFaltaAberto(true);
  };

  const onCellDoubleClicked = (params) => {
    if (params.column.getColId() !== 'data') return;
    setLinhaParaApagar(params.data);
    setDialogApagarAberto(true);
  };

  const confirmarApagarLinha = async () => {
    if (!linhaParaApagar) return;

    const underscoreIdx = linhaParaApagar.id.indexOf('_');
    const funcionario_id = Number(linhaParaApagar.id.substring(0, underscoreIdx));
    const dataRegistro = linhaParaApagar.id.substring(underscoreIdx + 1);

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/ponto-ponto-apagar-dia/`, {
        method: 'POST',
        body: JSON.stringify({ funcionario_id, data: dataRegistro }),
      });
      const json = await response.json();
      if (!response.ok) { alert(json.error || 'Erro ao apagar.'); return; }

      // Remove a linha localmente, sem precisar recarregar a tabela toda
      setRowData(prevData => prevData.filter(row => row.id !== linhaParaApagar.id));
    } catch (e) {
      console.error(e);
      alert('Erro ao apagar registro.');
    } finally {
      setDialogApagarAberto(false);
      setLinhaParaApagar(null);
    }
  };

  const handleSalvarFalta = async (payload) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/ponto-ponto-registrar-falta/`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const json = await response.json();
      if (!response.ok) { alert(json.erro || 'Erro ao salvar falta.'); return; }
      const linhaAtualizada = json.linha_atualizada;
      if (!linhaAtualizada) return;
      setRowData(prevData =>
        prevData.map(row => {
          const linhaId = `${linhaAtualizada.funcionario_id}_${linhaAtualizada.data}`;
          if (row.id === linhaId) return { ...row, falta: linhaAtualizada.falta_nome, falta_id: linhaAtualizada.falta_id, observacao: linhaAtualizada.observacao };
          return row;
        })
      );
      alert('Falta registrada com sucesso!');
    } catch (e) {
      console.error('Erro ao salvar falta:', e);
      alert('Erro ao salvar falta.');
    }
  };

  const fetchData = async (filtros = {}) => {
    setLoading(true);
    isLoadingRef.current = true;
    const payload = {
      funcionario_id: filtros.funcionario_id !== undefined ? filtros.funcionario_id : (funcionario || ''),
      mes: filtros.mes !== undefined ? filtros.mes : (mes || ''),
      ano: filtros.ano !== undefined ? filtros.ano : (ano || ''),
      filtro_ativo: filtroAtivo,
      data_inicial: filtroAtivo ? dataInicial.toISOString().split('T')[0] : null,
      data_final: filtroAtivo ? dataFinal.toISOString().split('T')[0] : null,
    };
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/ponto-ponto-dados/`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.error) { console.error('Erro da API:', data.error); return; }
      setFuncionariosOptions(data.opcoes?.funcionarios || []);
      setAnosOptions(data.opcoes?.anos || []);
      setMesesOptions(data.opcoes?.meses || []);
      if (filtros.funcionario_id === undefined && data.valores_selecionados?.funcionario_id)
        setFuncionario(data.valores_selecionados.funcionario_id);
      if (filtros.ano === undefined && data.valores_selecionados?.ano)
        setAno(data.valores_selecionados.ano);
      if (filtros.mes === undefined && data.valores_selecionados?.mes)
        setMes(data.valores_selecionados.mes);
      setRowData(data.dados_tabela || []);
    } catch (err) {
      if (err.message.includes('Token')) logout();
      else alert('Erro: ' + err.message);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (filtroAtivo) fetchData();
  }, [dataInicial, dataFinal, filtroAtivo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFuncionarioChange = (v) => {
    if (isLoadingRef.current) return;
    setFuncionario(v);
    fetchData({ funcionario_id: v });
  };
  const handleAnoChange = (v) => {
    if (isLoadingRef.current) return;
    setAno(v);
    fetchData({ ano: v });
  };
  const handleMesChange = (v) => {
    if (isLoadingRef.current) return;
    setMes(v);
    fetchData({ mes: v });
  };

  const getNomeMes = (numeroMes) => {
    const meses = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
    return meses[numeroMes - 1] || numeroMes;
  };

  const isEmptyOrMask = (valor) => !valor || valor.trim() === '' || valor.trim() === ':';

  // ========== VALIDAÇÃO ==========

  const validarCelula = (campo, newValue, data) => {
    const camposHorario = ['entrada','saidaIntervalo','entradaIntervalo','saida','ent','sai'];
    if (!camposHorario.includes(campo)) return null;

    if (!isEmptyOrMask(newValue)) {
      if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(newValue))
        return 'Horário inválido. Use HH:MM.';
    }

    const valores = {
      entrada: data.entrada, saidaIntervalo: data.saidaIntervalo,
      entradaIntervalo: data.entradaIntervalo, saida: data.saida,
      ent: data.ent, sai: data.sai, [campo]: newValue,
    };

    if (['entrada','saidaIntervalo','entradaIntervalo','saida'].includes(campo)) {
      const toMin = (h) => {
        if (isEmptyOrMask(h)) return null;
        const [hh, mm] = h.split(':').map(Number);
        return hh * 60 + mm;
      };
      const tE = toMin(valores.entrada);
      const tSI = toMin(valores.saidaIntervalo);
      const tEI = toMin(valores.entradaIntervalo);
      let tS = toMin(valores.saida);

      if (tE != null && tSI != null && tE > tSI) {
        return 'Entrada deve ser ≤ Saída Intervalo.';
      }

      if (tSI != null && tEI != null && tSI > tEI) {
        return 'Saída Intervalo deve ser ≤ Entrada Intervalo.';
      }

      // =====================================================
      // SAÍDA PODE SER NO DIA SEGUINTE
      // Considera virada de dia somente se:
      // 1. A saída estiver entre 00:00 e 05:00
      // 2. A saída for menor que a Entrada Intervalo
      // =====================================================
      if (tEI != null && tS != null) {

        const CINCO_HORAS = 5 * 60; // 300 minutos

        const saidaEhDeMadrugada = tS >= 0 && tS <= CINCO_HORAS;
        const saidaEhAntesDaEntradaIntervalo = tS < tEI;

        if (saidaEhDeMadrugada && saidaEhAntesDaEntradaIntervalo) {
          // A saída aconteceu no dia seguinte
          tS += 24 * 60;
        }

        if (tEI > tS) {
          return 'Entrada Intervalo deve ser ≤ Saída.';
        }
      }
    }
    return null;
  };

  // ========== SALVAR ALTERAÇÃO ==========

  const salvarAlteracao = async (payload, revert) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/ponto-ponto-editar-tabela/`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const json = await response.json();
      if (!response.ok) { revert(); alert(json.erro || 'Erro ao salvar.'); return; }
      const r = json.linha_atualizada;
      if (!r) return;

      setRowData(prevData =>
        prevData.map(row => {
          if (row.id !== `${r.funcionario_id}_${r.data}`) return row;
          return {
            ...row,
            entrada:          r.entrada          ?? row.entrada,
            saidaIntervalo:   r.saidaIntervalo   ?? row.saidaIntervalo,
            entradaIntervalo: r.entradaIntervalo  ?? row.entradaIntervalo,
            saida:            r.saida            ?? row.saida,
            ent:              r.ent              ?? row.ent,
            sai:              r.sai              ?? row.sai,
            horaExtra:   r.hora_extra_min,
            horaFalta:   r.hora_falta_min,
            horaNoturna: r.hora_noturna_min,
          };
        })
      );
    } catch (e) {
      console.error(e);
      revert();
    }
  };

  // ========== RECALCULAR ==========

  const handleRecalcular = async () => {
    if (!window.confirm('Recalcular hora extra, falta e noturna para todos os registros exibidos?')) return;
    setRecalculando(true);
    try {
      const payload = {
        funcionario_id: funcionario || null,
        mes: mes || null,
        ano: ano || null,
        filtro_ativo: filtroAtivo,
        data_inicial: filtroAtivo ? dataInicial.toISOString().split('T')[0] : null,
        data_final:   filtroAtivo ? dataFinal.toISOString().split('T')[0]   : null,
      };
      const response = await authenticatedFetch(`${API_BASE_URL}/api/ponto-ponto-recalcular/`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const json = await response.json();
      if (!response.ok) { alert(json.erro || 'Erro ao recalcular.'); return; }
      alert(`Recálculo concluído: ${json.total} registros atualizados.`);
      fetchData();
    } catch (e) {
      console.error(e);
      alert('Erro ao recalcular.');
    } finally {
      setRecalculando(false);
    }
  };

  // ========== EXPORTAR CSV (CORRIGIDO PARA MANTER A ORDEM DA TELA) ==========

  const handleExportarCSV = () => {
    if (!gridApiRef.current) {
      alert('Tabela não está pronta para exportação.');
      return;
    }

    // 1. Obter cabeçalhos das colunas visíveis
    const colunasVisiveis = columnDefs.filter(col => !col.hide);
    const cabecalho = colunasVisiveis.map(col => col.headerName).join(';');

    // 2. Extrair dados respeitando a ordenação e os filtros atuais da tela via API do AG-Grid
    const rows = [];
    gridApiRef.current.forEachNodeAfterFilterAndSort((node) => {
      const row = node.data;
      const linhaMapeada = colunasVisiveis.map(col => {
        const valor = row[col.field];
        if (valor === null || valor === undefined) return '';
        return String(valor).replace(/(\r\n|\n|\r)/gm, " ");
      }).join(';');
      rows.push(linhaMapeada);
    });

    if (rows.length === 0) {
      alert('Não há dados para exportar.');
      return;
    }

    // 3. Unir cabeçalho e dados em uma única string
    const csvString = [cabecalho, ...rows].join('\r\n');

    // 4. CONVERSÃO PARA LATIN-1 (ISO-8859-1) para Excel Brasileiro
    const charCodeArray = [];
    for (let i = 0; i < csvString.length; i++) {
      const code = csvString.charCodeAt(i);
      charCodeArray.push(code > 255 ? 63 : code); 
    }
    const uint8Array = new Uint8Array(charCodeArray);

    // 5. Download do Arquivo
    const blob = new Blob([uint8Array], { type: 'text/csv;charset=iso-8859-1;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const nomeMes = mes ? String(mes).padStart(2, '0') : 'todos';
    
    link.href = url;
    link.setAttribute('download', `ponto_tabela_${ano || 'todos'}_${nomeMes}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // ========== onCellValueChanged ==========

  const onCellValueChanged = (params) => {
    const colId = params.column.getColId();
    const campo = colId === 'observacaoId' ? 'observacao' : colId;

    const newValue = params.newValue ?? '';
    const oldValue = params.oldValue ?? '';

    if (newValue === oldValue) return;

    const underscoreIdx = params.data.id.indexOf('_');
    const funcionario_id = Number(params.data.id.substring(0, underscoreIdx));
    const data = params.data.id.substring(underscoreIdx + 1);

    const revert = () => {
      setRowData(prevData =>
        prevData.map(row =>
          row.id === params.data.id ? { ...row, [campo]: oldValue } : row
        )
      );
    };

    const erro = validarCelula(campo, newValue, params.data);
    if (erro) {
      alert(erro);
      revert();
      return;
    }

    setEditingLocked(true);

    const payload = {
      campo,
      valor: newValue,
      funcionario_id,
      data,
    };

    salvarAlteracao(payload, revert).finally(() => setEditingLocked(false));
  };

  return (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <ModalRegistrarFalta
        aberto={modalFaltaAberto}
        onFechar={() => setModalFaltaAberto(false)}
        linha={linhaEditando}
        listaFaltas={listaFaltas}
        onSalvar={handleSalvarFalta}
      />

      <Dialog open={dialogApagarAberto} onClose={() => setDialogApagarAberto(false)}>
        <DialogTitle>Apagar registro de ponto?</DialogTitle>
        <DialogContent>
          {linhaParaApagar && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography><b>Funcionário:</b> {linhaParaApagar.funcionario}</Typography>
              <Typography><b>Data:</b> {linhaParaApagar.data}</Typography>
              <Typography><b>Entrada:</b> {linhaParaApagar.entrada || '--'}</Typography>
              <Typography><b>Saída Intervalo:</b> {linhaParaApagar.saidaIntervalo || '--'}</Typography>
              <Typography><b>Entrada Intervalo:</b> {linhaParaApagar.entradaIntervalo || '--'}</Typography>
              <Typography><b>Saída:</b> {linhaParaApagar.saida || '--'}</Typography>
              <Typography><b>Observação:</b> {linhaParaApagar.observacao || '--'}</Typography>
              <Typography sx={{ mt: 2 }} color="error" fontWeight="bold">
                Esta ação não pode ser desfeita.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogApagarAberto(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmarApagarLinha}>Apagar</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Funcionários</InputLabel>
          <Select value={funcionario} label="Funcionários" onChange={(e) => handleFuncionarioChange(e.target.value)}>
            <MenuItem value=""><em>Todos</em></MenuItem>
            {funcionariosOptions.map((func) => (
              <MenuItem key={func.id} value={func.id}>{func.nome}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Mês</InputLabel>
          <Select value={mes} label="Mês" onChange={(e) => handleMesChange(e.target.value)}>
            <MenuItem value=""><em>Todos</em></MenuItem>
            {mesesOptions.map((mesOption) => (
              <MenuItem key={mesOption} value={mesOption}>{getNomeMes(mesOption)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Ano</InputLabel>
          <Select value={ano} label="Ano" onChange={(e) => handleAnoChange(e.target.value)}>
            <MenuItem value=""><em>Todos</em></MenuItem>
            {anosOptions.map((anoOption) => (
              <MenuItem key={anoOption} value={anoOption}>{anoOption}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <DatePicker
            label="Data inicial" value={dataInicial}
            onChange={(v) => setDataInicial(v)}
            renderInput={(params) => <TextField {...params} />}
          />
          <DatePicker
            label="Data final" value={dataFinal}
            onChange={(v) => setDataFinal(v)}
            renderInput={(params) => <TextField {...params} />}
          />
          <FormControlLabel
            control={<Switch checked={filtroAtivo} onChange={(e) => setFiltroAtivo(e.target.checked)} />}
            label="Ativar filtro por período"
          />
        </Box>
      </LocalizationProvider>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="outlined"
          color="warning"
          onClick={handleRecalcular}
          disabled={recalculando || loading || rowData.length === 0}
          startIcon={recalculando ? <CircularProgress size={16} /> : null}
        >
          {recalculando ? 'Recalculando...' : '🔄 Recalcular'}
        </Button>
        <Button
          variant="outlined"
          color="success"
          onClick={handleExportarCSV}
          disabled={loading || rowData.length === 0}
        >
          📥 Exportar CSV
        </Button>
      </Box>

      <Box style={{ height: 600, width: 1200, position: 'relative' }}>
        <style>{`
          .ag-tooltip {
            background-color: white !important;
            opacity: 1 !important;
            border: 1px solid #ccc !important;
            border-radius: 4px !important;
            padding: 8px !important;
            color: #333 !important;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1) !important;
          }
          .ag-theme-alpine .ag-header-row {
            height: 60px !important;
            min-height: 60px !important;
          }
        `}</style>

        {loading ? (
          <Typography>Carregando dados...</Typography>
        ) : (
          <>
            {editingLocked && (
              <div style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                background: 'rgba(255,255,255,0.6)', display: 'flex',
                justifyContent: 'center', alignItems: 'center',
                zIndex: 10, fontSize: '24px', fontWeight: 'bold',
              }}>
                Salvando...
              </div>
            )}

            <AgGridReact
              className="ag-theme-alpine"
              rowData={rowData}
              columnDefs={columnDefs}
              rowHeight={30}
              headerHeight={60}
              getRowId={(params) => params.data.id}
              domLayout="normal"
              multiSortKey='ctrl' // Permite ordenar por mais de uma coluna segurando Ctrl na tela
              defaultColDef={{
                suppressSizeToFit: true,
                flex: 0,
                cellStyle: { display: 'flex', alignItems: 'center', padding: '0 5px' },
                tooltipDelay: 300,
                tooltipMouseTrack: true,
              }}
              tooltipShowDelay={300}
              tooltipHideDelay={5000}
              onCellValueChanged={onCellValueChanged}
              onCellDoubleClicked={onCellDoubleClicked}
              onGridReady={(params) => { gridApiRef.current = params.api; }}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default PontoPonto;